/**
 * User Groups — Settings › User & Access › User Groups.
 *
 * A group is a named set of users an admin can grant modules to all at
 * once, instead of sharing one module with one user at a time (see
 * `moduleService.setModuleShare` for the direct-share equivalent). The two
 * are independent: a module can be shared with individual users AND granted
 * to groups, and neither list shows the other.
 *
 * Admin-only on the server; this file adds no gate of its own, matching
 * `userService`/`serverConnectionsService` — the screen that calls it is
 * already behind `isAdmin`.
 */

import type { Group, GroupDetail, MyGroup } from '../types';
import { api, ApiError } from './apiClient';

function describeFailure(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.unauthenticated) return 'Your session has expired. Sign in again.';
    if (error.status === 403) return 'Only an administrator can manage user groups.';
    if (error.status === 404) return 'This group no longer exists — it may have just been deleted.';
    if (error.status === 0) return 'Could not reach the server. Check your connection and try again.';
    return error.message;
  }
  return fallback;
}

export const groupService = {
  /** Groups the CALLER belongs to — the only group read a normal user is
   *  ever allowed, from their own Profile page. Unlike everything else in
   *  this file, this one endpoint is not admin-gated on the server. */
  async mine(): Promise<MyGroup[]> {
    try {
      return await api.get<MyGroup[]>('/groups/mine');
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not load your groups.'));
    }
  },

  async list(): Promise<Group[]> {
    try {
      return await api.get<Group[]>('/groups');
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not load groups.'));
    }
  },

  async get(id: string): Promise<GroupDetail> {
    try {
      return await api.get<GroupDetail>(`/groups/${id}`);
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not load this group.'));
    }
  },

  async create(name: string): Promise<GroupDetail> {
    try {
      return await api.post<GroupDetail>('/groups', { name: name.trim() });
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not create this group.'));
    }
  },

  async rename(id: string, name: string): Promise<GroupDetail> {
    try {
      return await api.put<GroupDetail>(`/groups/${id}`, { name: name.trim() });
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not rename this group.'));
    }
  },

  async remove(id: string): Promise<void> {
    try {
      await api.delete<void>(`/groups/${id}`);
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not delete this group.'));
    }
  },

  /** Replaces the group's member list wholesale — "these users, and only
   *  these", the same shape `moduleService.setModuleShare` uses. */
  async setMembers(id: string, userIds: string[]): Promise<GroupDetail> {
    try {
      return await api.put<GroupDetail>(`/groups/${id}/members`, { userIds });
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not update this group’s members.'));
    }
  },

  /** Replaces the group's module grants wholesale. */
  async setModules(id: string, moduleIds: string[]): Promise<GroupDetail> {
    try {
      return await api.put<GroupDetail>(`/groups/${id}/modules`, { moduleIds });
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not update this group’s applications.'));
    }
  },
};
